/**
 * TagPicker — Modal for editing tags with color swatches.
 * Shows existing tags with their colors, allows adding/removing tags,
 * and lets users override individual tag colors.
 */

import React, { useState, useEffect, useMemo, useId } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  StyleSheet,
  Pressable,
  Platform,
  Keyboard,
} from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, ThemeColors } from '../../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../utils/design-tokens';
import { getTagColor, SELECTABLE_PALETTE, RESERVED_TAG_COLORS } from '../../utils/tagColors';
import { useBackHandler } from '../../navigation/useBackHandler';

interface Props {
  visible: boolean;
  tags: string[];
  tagColors: Record<string, string> | null;
  suggestedTags?: string[];
  onDone: (tags: string[], tagColorUpdates?: Record<string, string>) => void;
  onCancel: () => void;
}

const COLOR_WHEEL_SIZE = 228;
const COLOR_WHEEL_DOT_SIZE = 18;

type ColorWheelPoint = {
  id: string;
  hex: string;
  left: number;
  top: number;
  size: number;
};

function toHexChannel(value: number): string {
  return Math.round(value).toString(16).padStart(2, '0').toUpperCase();
}

function hsvToHex(hue: number, saturation: number, value: number): string {
  const chroma = value * saturation;
  const huePrime = hue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePrime >= 0 && huePrime < 1) [red, green, blue] = [chroma, x, 0];
  else if (huePrime >= 1 && huePrime < 2) [red, green, blue] = [x, chroma, 0];
  else if (huePrime >= 2 && huePrime < 3) [red, green, blue] = [0, chroma, x];
  else if (huePrime >= 3 && huePrime < 4) [red, green, blue] = [0, x, chroma];
  else if (huePrime >= 4 && huePrime < 5) [red, green, blue] = [x, 0, chroma];
  else [red, green, blue] = [chroma, 0, x];

  const match = value - chroma;
  return `#${toHexChannel((red + match) * 255)}${toHexChannel((green + match) * 255)}${toHexChannel((blue + match) * 255)}`;
}

function buildColorWheelPoints(): ColorWheelPoint[] {
  const center = COLOR_WHEEL_SIZE / 2;
  const maxRadius = (COLOR_WHEEL_SIZE - COLOR_WHEEL_DOT_SIZE) / 2;
  const points: ColorWheelPoint[] = [{ id: 'center', hex: '#FFFFFF', left: center - (COLOR_WHEEL_DOT_SIZE / 2), top: center - (COLOR_WHEEL_DOT_SIZE / 2), size: COLOR_WHEEL_DOT_SIZE }];

  for (let ring = 1; ring <= 7; ring += 1) {
    const saturation = ring / 7;
    const radius = maxRadius * saturation;
    const count = 12 + (ring * 6);
    for (let index = 0; index < count; index += 1) {
      const angle = (index / count) * Math.PI * 2;
      const hue = (index / count) * 360;
      points.push({
        id: `${ring}-${index}`,
        hex: hsvToHex(hue, saturation, 1),
        left: center + (Math.cos(angle) * radius) - (COLOR_WHEEL_DOT_SIZE / 2),
        top: center + (Math.sin(angle) * radius) - (COLOR_WHEEL_DOT_SIZE / 2),
        size: COLOR_WHEEL_DOT_SIZE,
      });
    }
  }

  return points;
}

const COLOR_WHEEL_POINTS = buildColorWheelPoints();

function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(trimmed)) {
    return `#${trimmed.split('').map((char) => char + char).join('').toUpperCase()}`;
  }
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return null;
}

export default function TagPicker({ visible, tags: initialTags, tagColors, suggestedTags = [], onDone, onCancel }: Props) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();
  const [tags, setTags] = useState<string[]>(initialTags);
  const [input, setInput] = useState('');
  const [editingColor, setEditingColor] = useState<string | null>(null);
  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>({});
  const [customColorTag, setCustomColorTag] = useState<string | null>(null);
  const [customHexInput, setCustomHexInput] = useState('#2196F3');
  const insets = useSafeAreaInsets();
  const keyboardHeight = useSharedValue(0);
  const normalizedCustomHex = normalizeHexColor(customHexInput);

  // Smooth keyboard tracking via Reanimated (avoids KAV jitter on Android)
  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      keyboardHeight.value = withTiming(e.endCoordinates.height, { duration: 250 });
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      keyboardHeight.value = withTiming(0, { duration: 250 });
    });
    return () => { showSub.remove(); hideSub.remove(); };
  }, []);

  const animatedWrapStyle = useAnimatedStyle(() => ({
    paddingBottom: keyboardHeight.value,
  }));

  // Reset internal state whenever the picker opens
  useEffect(() => {
    if (visible) {
      setTags(initialTags);
      setInput('');
      setEditingColor(null);
      setColorOverrides({});
      setCustomColorTag(null);
      setCustomHexInput('#2196F3');
    }
  }, [visible]);

  const usedTagColors = useMemo(() => {
    const used = new Set<string>();
    Object.values(tagColors || {}).forEach((color) => {
      if (color) used.add(color);
    });
    suggestedTags.forEach((tag) => {
      const normalized = tag.toLowerCase().trim();
      if (normalized) used.add(getTagColor(normalized, tagColors));
    });
    tags.forEach((tag) => {
      const normalized = tag.toLowerCase().trim();
      if (!normalized) return;
      used.add(colorOverrides[normalized] || getTagColor(normalized, tagColors));
    });
    return used;
  }, [colorOverrides, suggestedTags, tagColors, tags]);

  const getUnusedDefaultColor = () => {
    return SELECTABLE_PALETTE.find((color) => !RESERVED_TAG_COLORS.has(color) && !usedTagColors.has(color))
      || SELECTABLE_PALETTE.find((color) => !RESERVED_TAG_COLORS.has(color))
      || SELECTABLE_PALETTE[0];
  };

  const addTag = () => {
    const t = input.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setColorOverrides({ ...colorOverrides, [t]: getUnusedDefaultColor() });
      setEditingColor(t);
      setCustomColorTag(null);
    }
    setInput('');
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
    const normalized = tag.toLowerCase().trim();
    setColorOverrides((current) => {
      const remaining = { ...current };
      delete remaining[normalized];
      return remaining;
    });
    if (editingColor === tag) setEditingColor(null);
    if (customColorTag === tag) setCustomColorTag(null);
  };

  const setTagColor = (tag: string, color: string) => {
    const normalizedColor = normalizeHexColor(color) || color;
    setColorOverrides({ ...colorOverrides, [tag.toLowerCase().trim()]: normalizedColor });
    setEditingColor(null);
    setCustomColorTag(null);
  };

  const openCustomColorPicker = (tag: string, color: string) => {
    setCustomColorTag(customColorTag === tag ? null : tag);
    setCustomHexInput(normalizeHexColor(color) || '#2196F3');
  };

  const applyCustomColor = (tag: string) => {
    if (!normalizedCustomHex) return;
    setTagColor(tag, normalizedCustomHex);
  };

  const handleDone = () => {
    const selectedTagKeys = new Set(tags.map((tag) => tag.toLowerCase().trim()).filter(Boolean));
    const selectedColorOverrides = Object.entries(colorOverrides).reduce<Record<string, string>>((updates, [tag, color]) => {
      if (selectedTagKeys.has(tag)) updates[tag] = color;
      return updates;
    }, {});
    const updates = Object.keys(selectedColorOverrides).length > 0 ? selectedColorOverrides : undefined;
    onDone(tags, updates);
  };

  useBackHandler({
    id: `tag-picker-${modalId}`,
    label: 'Tag picker',
    priority: 950,
    enabled: visible,
    onBack: () => {
      onCancel();
      return true;
    },
  });

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <Animated.View style={[s.kavOuter, animatedWrapStyle]}>
      <Pressable style={s.overlay} onPress={onCancel}>
        <Pressable style={[s.sheet, { paddingBottom: Math.max(insets.bottom, 16) }]} onPress={(e) => e.stopPropagation()}>
          <View style={s.header}>
            <Text style={s.title}>Tags</Text>
            <View style={s.headerActions}>
              <TouchableOpacity onPress={onCancel} style={s.cancelButton} accessibilityRole="button" accessibilityLabel="Cancel tag changes">
                <Text style={s.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleDone} style={s.doneButton} accessibilityRole="button" accessibilityLabel="Save tag changes">
                <Text style={s.doneButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Input for new tags */}
          <View style={s.inputRow}>
            <TextInput
              style={s.input}
              value={input}
              onChangeText={setInput}
              placeholder="Add tag..."
              placeholderTextColor={colors.textSecondary}
              onSubmitEditing={addTag}
              returnKeyType="done"
              autoCapitalize="none"
            />
            <TouchableOpacity onPress={addTag} style={s.addBtn}>
              <Ionicons name="add-circle" size={28} color={colors.primary} />
            </TouchableOpacity>
          </View>

          {/* Suggestions — previously used tags not already selected */}
          {(() => {
            const available = suggestedTags.filter(sug => !tags.includes(sug.toLowerCase().trim()));
            if (available.length === 0) return null;
            return (
              <View style={s.suggestionsRow}>
                <Text style={s.suggestionsLabel}>Suggestions</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.suggestionsScroll}>
                  {available.map((sug) => {
                    const c = getTagColor(sug, tagColors);
                    return (
                      <TouchableOpacity
                        key={sug}
                        style={[s.suggestionChip, { borderColor: c + '40', backgroundColor: c + '12' }]}
                        onPress={() => { if (!tags.includes(sug)) setTags([...tags, sug]); }}
                        accessibilityRole="button"
                        accessibilityLabel={`Add ${sug} tag`}
                      >
                        <View style={[s.colorDot, { backgroundColor: c, width: 8, height: 8, borderRadius: 4 }]} />
                        <Text style={[s.suggestionText, { color: c }]}>{sug}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            );
          })()}

          {/* Tag chips */}
          <ScrollView style={s.tagList} contentContainerStyle={s.tagListContent}>
            {tags.map((tag) => {
              const normalizedKey = tag.toLowerCase().trim();
              const color = colorOverrides[normalizedKey] || getTagColor(tag, tagColors);
              return (
                <View key={tag}>
                  <View
                    style={[s.tagChip, { borderColor: color, backgroundColor: color + '18' }]}
                  >
                    <TouchableOpacity
                      style={s.tagColorButton}
                      onPress={() => setEditingColor(editingColor === tag ? null : tag)}
                      accessibilityRole="button"
                      accessibilityLabel={`Change ${tag} color`}
                    >
                      <View style={[s.colorDot, { backgroundColor: color }]} />
                      <Ionicons name="color-palette-outline" size={14} color={color} />
                    </TouchableOpacity>
                    <Text style={[s.tagText, { color }]}>{tag}</Text>
                    <TouchableOpacity
                      onPress={() => removeTag(tag)}
                      style={s.removeTagButton}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      accessibilityRole="button"
                      accessibilityLabel={`Remove ${tag}`}
                    >
                      <Ionicons name="trash-outline" size={15} color={colors.danger} />
                    </TouchableOpacity>
                  </View>

                  {/* Color palette (shown when editing this tag's color) */}
                  {editingColor === tag && (() => {
                    // Collect colors already assigned to OTHER tags
                    const usedByOthers = new Set<string>();
                    for (const t of tags) {
                      if (t === tag) continue;
                      const k = t.toLowerCase().trim();
                      const assigned = colorOverrides[k] || getTagColor(t, tagColors);
                      usedByOthers.add(assigned);
                    }
                    return (
                      <View style={s.palette}>
                        {SELECTABLE_PALETTE.map((c) => {
                          const isReserved = RESERVED_TAG_COLORS.has(c);
                          const isDuplicate = usedByOthers.has(c);
                          const disabled = isReserved || isDuplicate;
                          return (
                            <TouchableOpacity
                              key={c}
                              style={[
                                s.swatch,
                                { backgroundColor: c },
                                c === color && s.swatchSelected,
                                disabled && s.swatchDisabled,
                              ]}
                              onPress={() => { if (!disabled) setTagColor(tag, c); }}
                              activeOpacity={disabled ? 1 : 0.7}
                            />
                          );
                        })}
                        <TouchableOpacity
                          style={[s.swatch, s.customSwatch]}
                          onPress={() => openCustomColorPicker(tag, color)}
                          activeOpacity={0.75}
                          accessibilityRole="button"
                          accessibilityLabel="Choose custom color"
                        >
                          <Ionicons name="add" size={18} color="#FFFFFF" />
                        </TouchableOpacity>
                      </View>
                    );
                  })()}
                  {customColorTag === tag && (
                    <View style={s.customColorPanel}>
                      <View style={s.customColorHeader}>
                        <View style={[s.customColorPreview, { backgroundColor: normalizedCustomHex || color }]} />
                        <TextInput
                          style={s.hexInput}
                          value={customHexInput}
                          onChangeText={setCustomHexInput}
                          placeholder="#RRGGBB"
                          placeholderTextColor={colors.textSecondary}
                          autoCapitalize="characters"
                          autoCorrect={false}
                          maxLength={7}
                        />
                        <TouchableOpacity
                          style={[s.applyColorButton, !normalizedCustomHex && s.applyColorButtonDisabled]}
                          onPress={() => applyCustomColor(tag)}
                          disabled={!normalizedCustomHex}
                          activeOpacity={0.75}
                        >
                          <Text style={s.applyColorText}>Apply</Text>
                        </TouchableOpacity>
                      </View>
                      <View style={s.colorWheelWrap}>
                        <View style={s.colorWheel}>
                          {COLOR_WHEEL_POINTS.map((point) => (
                          <TouchableOpacity
                            key={point.id}
                            style={[
                              s.colorWheelDot,
                              {
                                backgroundColor: point.hex,
                                left: point.left,
                                top: point.top,
                                width: point.size,
                                height: point.size,
                                borderRadius: point.size / 2,
                              },
                              normalizedCustomHex === point.hex && s.colorWheelDotSelected,
                            ]}
                            onPress={() => setCustomHexInput(point.hex)}
                            activeOpacity={0.75}
                            accessibilityRole="button"
                            accessibilityLabel={`Use color ${point.hex}`}
                          />
                        ))}
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
            {tags.length === 0 && (
              <Text style={s.emptyText}>No tags yet — type above to add one</Text>
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
      </Animated.View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  kavOuter: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: colors.card,
    borderTopLeftRadius: RADIUS.xl,
    borderTopRightRadius: RADIUS.xl,
    paddingTop: SPACING.base,
    paddingBottom: SPACING.base,  // base — overridden inline with insets.bottom
    paddingHorizontal: SPACING.lg,
    minHeight: '50%',
    maxHeight: '90%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.base,
  },
  title: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  cancelButton: {
    paddingHorizontal: SPACING.sm,
    paddingVertical: 8,
  },
  cancelButtonText: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  doneButton: {
    paddingHorizontal: SPACING.md,
    paddingVertical: 8,
    borderRadius: RADIUS.full,
    backgroundColor: colors.primary,
  },
  doneButtonText: {
    fontSize: FONT_SIZE.base,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    marginBottom: SPACING.base,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.base,
    color: colors.text,
    backgroundColor: colors.background,
  },
  addBtn: {
    padding: 4,
  },
  tagList: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 120,
  },
  tagListContent: {
    gap: SPACING.sm,
    paddingBottom: SPACING.base,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    gap: SPACING.sm,
  },
  tagColorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingRight: SPACING.sm,
    borderRightWidth: 1,
    borderRightColor: colors.border,
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  tagText: {
    flex: 1,
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
  },
  removeTagButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.card,
  },
  palette: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.sm,
    paddingVertical: SPACING.sm,
    paddingLeft: SPACING.md,
  },
  swatch: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  swatchSelected: {
    borderWidth: 3,
    borderColor: colors.text,
  },
  swatchDisabled: {
    opacity: 0.25,
  },
  customSwatch: {
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
  },
  customColorPanel: {
    marginLeft: SPACING.md,
    marginRight: SPACING.xs,
    marginBottom: SPACING.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: RADIUS.lg,
    backgroundColor: colors.background,
    padding: SPACING.md,
    gap: SPACING.sm,
  },
  customColorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  customColorPreview: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: colors.border,
  },
  hexInput: {
    flex: 1,
    minWidth: 0,
    height: 38,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
    fontSize: FONT_SIZE.sm,
    fontWeight: '700',
    color: colors.text,
    backgroundColor: colors.card,
  },
  applyColorButton: {
    height: 38,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACING.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  applyColorButtonDisabled: {
    opacity: 0.4,
  },
  applyColorText: {
    color: '#FFFFFF',
    fontSize: FONT_SIZE.xs,
    fontWeight: '700',
  },
  colorWheelWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.sm,
  },
  colorWheel: {
    width: COLOR_WHEEL_SIZE,
    height: COLOR_WHEEL_SIZE,
    borderRadius: COLOR_WHEEL_SIZE / 2,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.border,
    position: 'relative',
    overflow: 'hidden',
  },
  colorWheelDot: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
  },
  colorWheelDotSelected: {
    borderWidth: 3,
    borderColor: colors.text,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: FONT_SIZE.sm,
    textAlign: 'center',
    paddingVertical: SPACING.lg,
  },
  suggestionsRow: {
    marginBottom: SPACING.md,
  },
  suggestionsLabel: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
    color: colors.textSecondary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  suggestionsScroll: {
    gap: 6,
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    borderRadius: RADIUS.full,
    borderWidth: 1,
    gap: 5,
  },
  suggestionText: {
    fontSize: FONT_SIZE.xs,
    fontWeight: '600',
  },
});
